import {
  mapDatasourceUsers,
  mapQueryDrivesList,
  mapQueryDrivesOwners,
  mapQueryWorkdayOwners,
  queryDatasourceUsers,
  queryDrivesList,
  queryDrivesListCompact,
  queryDrivesOwners,
  queryWorkdayOwners
} from './queries'
import { Drive, GDriveWidget, GDriveWidgetQueryParams } from '../../services/api/apiTypes'
import service from '../../services/api/apiService'
import { getSortDirection, SortParams } from '../../utils/sortUtil'
import { IGetDrivesParams, SORT_ORDER } from '../../interfaces'
import graphqlService from '../../services/graphqlService'
import { RootState } from '../../rootReducer'
import { getGlobalParams } from '../../utils/urlUtil'
import { IGetPaginatedFiltersParams } from '../filters/filtersSlice'
import type { FilterValuesData } from '../filters/filters'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export type DrivesListSettings = {
  list?: Drive[]
  total?: number
  sort: SortParams
}

interface GoogleDriveDashboardState {
  widget: GDriveWidget | null
  list: DrivesListSettings
  owners?: string[]
  ownersFilter?: string[]
  ownersCount?: number
  copilotOwnersFilter?: FilterValuesData[]
  workdayOwnersFilter?: FilterValuesData[]
  workdayOwnersFilterCount?: number
  copilotOwnersFilterCount?: number
}

const initialList: DrivesListSettings = {
  sort: {
    column: 'driveName',
    direction: SORT_ORDER.ASC
  }
}

export const initialState: GoogleDriveDashboardState = {
  widget: null,
  list: { ...initialList }
}

export const fetchDrivesWidget = createAsyncThunk(
  'drives/widget',
  async (filters: GDriveWidgetQueryParams) => await service.getDrivesWidget(filters || {})
)

export const ACTION_DRIVES_FETCH = 'drives/list'
export const fetchDrivesList = createAsyncThunk(
  ACTION_DRIVES_FETCH,
  async (params: IGetDrivesParams, { getState }) => {
    const queryParams = { ...params, ...getGlobalParams(getState() as RootState) }
    const resultRaw = await graphqlService.execute(queryDrivesList(queryParams))
    return mapQueryDrivesList(resultRaw)
  }
)

export const ACTION_DRIVES_COMPACT_FETCH = 'drives/list/compact'
export const fetchDrivesListCompact = createAsyncThunk(
  ACTION_DRIVES_COMPACT_FETCH,
  async (params: IGetDrivesParams) => {
    const resultRaw = await graphqlService.execute(queryDrivesListCompact(params))
    return mapQueryDrivesList(resultRaw)
  }
)

export const ACTION_DRIVES_OWNERS_FETCH = 'drives/owners'
export const fetchDrivesOwners = createAsyncThunk(
  ACTION_DRIVES_OWNERS_FETCH,
  async (params: IGetPaginatedFiltersParams) => {
    const resultRaw = await graphqlService.execute(queryDrivesOwners(params))
    return mapQueryDrivesOwners(resultRaw)
  }
)

export type FetchCopilotOwnersParams = Pick<
  IGetPaginatedFiltersParams,
  'datasourceId' | 'pageSize' | 'page'
>
export const ACTION_FETCH_COPILOT_OWNERS_FILTER = 'copilot/owners-filter'
export const fetchCopilotOwnersFilter = createAsyncThunk(
  ACTION_FETCH_COPILOT_OWNERS_FILTER,
  async (params: FetchCopilotOwnersParams) => {
    const resultRaw = await graphqlService.execute(queryDatasourceUsers(params))

    return mapDatasourceUsers(resultRaw)
  }
)

export type FetchWorkdayOwnersParams = Pick<
  IGetPaginatedFiltersParams,
  'datasourceId' | 'pageSize' | 'page'
>
export const ACTION_FETCH_WORKDAY_OWNERS_FILTER = 'workday/owners-filter'
export const fetchWorkdayOwnersFilter = createAsyncThunk(
  ACTION_FETCH_WORKDAY_OWNERS_FILTER,
  async (params: FetchWorkdayOwnersParams) => {
    const resultRaw = await graphqlService.execute(queryWorkdayOwners(params))
    return mapQueryWorkdayOwners(resultRaw)
  }
)

const drivesSlice = createSlice({
  name: 'drives',
  initialState,
  reducers: {
    setSort: (state, { payload }) => {
      state.list.sort = getSortDirection(state.list.sort, payload.column)
    },
    resetWidget: (state) => {
      state.widget = initialState.widget
    },
    clearList: (state) => {
      state.list = initialState.list
    },
    resetDriveOwnersFilter: (state) => {
      state.ownersFilter = []
      state.ownersCount = 0
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDrivesWidget.fulfilled, (state, action) => {
      state.widget = action.payload
    })
    builder.addCase(fetchDrivesList.fulfilled, (state, { payload }) => {
      const { list, total } = payload
      state.list.list = list
      state.list.total = total
    })
    builder.addCase(fetchDrivesListCompact.fulfilled, (state, { payload }) => {
      const { list, total } = payload
      state.list.list = list
      state.list.total = total
    })
    builder.addCase(fetchDrivesOwners.fulfilled, (state, { payload, meta }) => {
      const resetOnFirstSearch =
        meta.arg.page === 1 && meta.arg.searchFilter && meta.arg.searchFilter.length > 0
      state.owners = payload.list
      state.ownersFilter = resetOnFirstSearch
        ? payload.list
        : [...(state.ownersFilter ?? []), ...payload.list]
      state.ownersCount = payload.total
    })
    builder.addCase(fetchCopilotOwnersFilter.fulfilled, (state, { payload }) => {
      state.copilotOwnersFilter = [...(state.copilotOwnersFilter ?? []), ...payload.datasourceUsers]
      state.copilotOwnersFilterCount = payload.count
    })
    builder.addCase(fetchWorkdayOwnersFilter.fulfilled, (state, { payload }) => {
      state.workdayOwnersFilter = [...(state.workdayOwnersFilter ?? []), ...payload.workdayOwners]
      state.workdayOwnersFilterCount = payload.workdayOwnersCount
    })
  }
})

export const { setSort, resetWidget, clearList, resetDriveOwnersFilter } = drivesSlice.actions

export default drivesSlice.reducer
